<script lang="ts">
  import { ProductTags, ProductType, type ProductData } from "$lib/def/landing";
  import type { IContextMenuItem } from "$lib/def/util-types";
  import { profile$ } from "$lib/serv/core/auth";
	import { PNP_IMG_HOST, PNP_IMGHD_HOST } from "$lib/serv/core/env";
  import { DEFAULT_MERCHANT_KEY, getCartStream, observeUpdateQtyRequests, updateCartItemQuantity } from "$lib/serv/groc-cart";
  import { getSavedStatusUpdates, updateSavedStatus } from "$lib/serv/groc-product";
  import { AlertType, setToastMessage } from "$lib/serv/toast";
  import ContextMenu from "$lib/ui/shared/ContextMenu.svelte";
  import LazyBackgroundImage from "$lib/ui/shared/LazyBackgroundImage.svelte";
  import Quantity from "$lib/ui/shared/Quantity.svelte";
  import { formatCurrency } from "$lib/util/format";
  import { Subject, distinctUntilChanged, firstValueFrom, map, shareReplay, startWith, switchMap, take, takeUntil, tap } from "rxjs";
  import { createEventDispatcher, onDestroy } from "svelte";

	/** Context menu items; these are updated each time the product is un/saved */
	let menuItems: IContextMenuItem[] = [];
	/** `item$` (defined below) is a hot observable, so we need to make sure it completes */
	let forceComplete$ = new Subject<void>();

	/** The initial details about the product provided to this component */
	export let item: ProductData;
	export let asBackup = false;
  export let isSelected = false;
	
	/**
	 * `item` needs to be dynamic because properties of the product can be changed by the user,
	 * e.g. its saved-ness
	 * */
	$: item$ = getSavedStatusUpdates(item.catalogue_key) // Could merge with other streams in future
		.pipe(
			startWith(item),
			takeUntil(forceComplete$),
			tap((product) => {
				// Update context menu options depending on whether the product is saved or not
				menuItems = [
					product.user_specific?.saved_list
						? { id: "unsave", label: "Remove from Saved" }
						: { id: "save", label: "Save" },
				];
			}),
			shareReplay(1),
		);
	/**
	 * In some contexts, there might be some product info that we don't want to display on the
	 * product tile, e.g. on the Buy Again view, we don't want to show the "Buy Again" tag
	 */
	export let hiddenTags: ProductTags[] = [];

	const dispatch = createEventDispatcher();

	$: quantity$ = createItemStream(item.catalogue_key);

	function createItemStream(id:string) {
		return getCartStream().pipe(
			map(cart => cart?.items?.find(i => i.catalogue_key === id)),
			map(item => ({ quantity: item?.quantity ?? 0})),
			distinctUntilChanged(), // this is testing object equality
			shareReplay(1),
		);
	}

	// Truncate item descriptions
	function truncateMiddle(text: string) {
		if (text.length <= 20) return text;
		const firstHalf = text.slice(0, 18);
		const secondHalf = text.slice(text.length - 5);
		return `${firstHalf}...${secondHalf}`;
	}

	// ####### CONTEXT MENU #######
	/** Should only be enabled when the user is logged in */
	const contextMenuEnabled$ = profile$.pipe(map((user) => !!user));

	/**
	 * Indicates where in the viewport the user right-clicked.
	 * Setting it to null hides the context menu.
	 * */
	let clickClientY: number | null = null;
	function showContextMenu(e: MouseEvent) {
		clickClientY = e.clientY;
	}

	const editSavedStatus = async (action: "save" | "unsave") => {
		try {
			const res = await firstValueFrom(item$.pipe(
				take(1),
				switchMap((product) => updateSavedStatus(product, action)),
			));
			const msg = action === "save" ? "Item saved" : "Item removed from Saved";
			setToastMessage(msg, AlertType.Info, 3000, `${PNP_IMG_HOST}/${item.url}`);
		} catch (error) {
			const msg = action === "save" ? "Unable to save item" : "Unable to remove item from Saved";
			setToastMessage(msg, AlertType.Info, undefined, `${PNP_IMG_HOST}/${item.url}`);
		}
	};

	// $: qty$ = !item?.catalogue_key ? EMPTY : merge(
	// 	streamActiveCartItemQty(item.catalogue_key).pipe(
	// 		map(value => ({ value, busy: false })),
	// 	),
	// 	subjQtyReq.pipe(
	// 		switchMap(value => delayQtyReq(value)),
	// 	),
	// );

	// let update: Awaited<ReturnType<typeof createCartUpdater>>['update'];
	// let end: Awaited<ReturnType<typeof createCartUpdater>>['end'];
	// createCartUpdater().then(res => ({update,end} = res));
	
	function delayQtyReq(value: number) {
		// return of(value).pipe(
		// 	delay(1000),
		// 	distinctUntilChanged(),
		// 	tap(q => update(item.catalogue_key, q)),
		// 	map(q => ({ value: q, busy: true })),
		// 	startWith({ value, busy: false }),
		// )
	}

	const itemUpdates$ = observeUpdateQtyRequests(item.catalogue_key);

	onDestroy(() => {
		// end?.();
		forceComplete$.next();
		forceComplete$.complete();
	});
</script>

<!-- example of realtime product quantity updates -->
<!-- {#if $itemUpdates$}
	{@const update = $itemUpdates$}
	|{item.catalogue_key}: {update?.event === 'started' ? 'UPDATING' : 'idle'}|
{/if} -->

{#if $item$}
{@const _item = $item$}
{@const contextMenuEnabled = $contextMenuEnabled$}

<div
	class="item relative"
	class:sold-out={!_item.in_stock}
	class:backupSelected={isSelected}
	on:keypress
	on:click={() => dispatch('product', isSelected ? null : _item)}
	on:contextmenu={(e) => { e.preventDefault(); if (contextMenuEnabled) showContextMenu(e); }}
	data-testid={`product-${_item.product_code}`}
	role="button"
	tabindex="0"
>
	<LazyBackgroundImage image={`${PNP_IMG_HOST}/${_item.url}`} alt={_item.label}>
		{#if _item.product_type === ProductType.Combo }
			<span class='orange-tag' class:sold-out={!item.in_stock}>Combo</span>
		{/if}
		{#if _item.is_alcohol }
			<span class='adult-tag'>18+</span>
		{/if}
		{#if _item.in_stock}
			{#if _item.user_specific?.last_bought && !hiddenTags.includes(ProductTags.BuyAgain)}
			<span class="buy-again label-xs medium">Buy Again</span>
			{/if}
			{#if !asBackup && $quantity$}
				{@const { quantity } = $quantity$}
				<Quantity {quantity} max={_item.maximum_order_quantity} on:update={e => updateCartItemQuantity(DEFAULT_MERCHANT_KEY, _item.catalogue_key, e.detail)} />
			{/if}
      {#if asBackup}
        <button class="backup-btn" class:selected={isSelected} on:click={() => dispatch('backup', isSelected ? null : _item)} data-testid="select-backup">
          <!-- {#if backup}
            <span class="icon-check-20"></span>
          {/if} -->
          <span></span>
          { isSelected ? 'Backup' : 'Select' }
        </button> 
      {/if}
		{:else}
			<article class="cover">
				<span class="label-s bold nowrap">Sold out</span>
			</article>
		{/if}
	</LazyBackgroundImage>
	<!-- {_item.label}
	<button on:click|stopPropagation={() => dispatch('alt', _item)}>Choose backup &rightarrow;</button> -->
	<!-- <div class="image" style={`background-image: url(${PUBLIC_PNP_IMG_HOST}/${_item.url})`}></div> -->
	<article>
		<!-- <img src={`${PUBLIC_PNP_IMG_HOST}/${_item.url}`} alt={_item.description}> -->
		<div>
				{#if _item.in_stock}
				<article>
					<!-- {#if _item.isBuyAgain}
					<span>Buy Again</span>
					{/if} -->
					<!-- <AddToCart /> -->
				</article>
				{/if}
		</div>
	</article>
	<div class="item-text">
		{#if _item.price_was}
			<div class="wasnow">
				<h3 class="label-xl m-0" data-testid="price">{formatCurrency(_item.price_now)}</h3>
				<s class="label-xl m-0 medium-importance">{formatCurrency(_item.price_was)}</s>
			</div>
			{:else}
			<div><h3 class="label-xl m-0" data-testid="price">{formatCurrency(_item.price_now)}</h3></div>
		{/if}
		<!-- {#each _item.tags as tag}
			<span class="tag">{tag}</span>
		{/each} -->
		{#if _item.label}
			<!-- {@const short_text = truncateMiddle(_item.label)} -->
			<p class="description label-m ellipses-3" data-testid="label">{_item.label}</p>
		{/if}
		<span>{_item.stock_status_message}</span>
	</div>

	{#if typeof clickClientY === "number"}
		<ContextMenu {menuItems} {clickClientY}
			on:hide={() => { clickClientY = null; }}
			on:clicked={({ detail: action }) => { clickClientY = null; editSavedStatus(action); }}
			id="product-context-menu"
		/>
	{/if}
</div>
{/if}

<!-- <div>
	<img src="{PUBLIC_PNP_IMG_HOST}/{_item.url}" height="32" alt="" on:click={() => dispatch('product', _item)} on:keypress>
	{#if $qty$}
		{@const qty = $qty$}
		{#if qty.value}
			<button on:click={() => qty.busy || subjQtyReq.next(qty.value - 1)} disabled={qty.busy}>&minus;</button>
			{ qty.value }
		{/if}
		<button on:click={() => qty.busy || subjQtyReq.next(qty.value + 1)} disabled={qty.busy}>&plus;</button>
	{/if}
	{_item.label}
	<button on:click={() => dispatch('alt', _item)}>Choose backup &rightarrow;</button>
</div> -->

<style lang="scss">
  @use "color";

  .backup-btn {
    position: absolute;
    bottom: 0;
    right: 0;
    background-color: color.$background-elevation-12dp;
    border: 2px solid color.$on-background-high-importance;
    border-radius: 14px;
    font-weight: 700;
    padding: 12px;
    z-index: 3;

    &:hover {
      cursor: pointer;
    }

    &.selected {
      background-color: color.$on-background-high-importance;
      border-color: color.$background;
      color: color.$background;
			
    }
  }
</style>
